"use client";

import { useEffect, useState } from "react";
import { useSidebar } from "../ui/sidebar";
import { get } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";

const StatisticsData = ({ game_id }) => {
  const { dates } = useSidebar();
  const [statistics, setStatistics] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchData = async () => {
      if (game_id === null) return;

      const data = await get(`/statistics?method=getGeneralStatistics&gameid=${game_id}&date=${format(selectedDate, "yyyy-MM-dd")}`);

      setStatistics(data);
    };

    fetchData();
  }, [selectedDate, game_id]);

  const changeDate = (date) => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[1]);
    if (date < startDate) {
      date = startDate;
    } else if (date > endDate) {
      date = endDate;
    }
    setSelectedDate(date);
  };

  return (
    <Card className="w-full border-0 shadow-md dark:bg-[var(--background-dark)]">
      <CardHeader className={`flex flex-row items-center justify-between space-y-0 pb-2 ${isMobile ? 'flex-col space-y-2' : ''}`}>
        <CardTitle className={`text-sm font-medium ${isMobile ? 'text-xs' : ''}`}>Statistics Data</CardTitle>
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant={"outline"} 
              className={cn(
                `${isMobile ? 'w-full text-xs' : 'w-[280px]'} justify-start text-left font-normal`, 
                !selectedDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
              {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className={`w-auto p-0 ${isMobile ? 'w-[280px]' : ''}`}>
            <Calendar mode="single" selected={selectedDate} onSelect={changeDate} initialFocus />
          </PopoverContent>
        </Popover>
      </CardHeader>
      <CardContent className={isMobile ? 'p-2' : ''}>
        <Table className={isMobile ? 'text-xs' : ''}>
          <TableHeader>
            <TableRow>
              <TableHead className={`dark:text-white ${isMobile ? 'px-2 py-1' : ''}`}>Statistic</TableHead>
              <TableHead className={`text-right dark:text-white ${isMobile ? 'px-2 py-1' : ''}`}>Metric</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(statistics).map(([key, value], index) => (
              <TableRow key={index}>
                <TableCell className={`font-medium dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>
                  {key === "date" ? "Date" : key}
                </TableCell>
                <TableCell className={`text-right dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>
                  {value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default StatisticsData;
